import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, HandCoins } from 'lucide-react';
import type { PrizeRedemptionDto, PrizeRedemptionStatus } from '@gc/shared';

import { ApiError } from '@/lib/api';
import { ghostPillClass, inputClass, pillClass } from '@/features/settings/controls';
import { useResource } from '@/features/settings/useSettings';
import {
  DEFAULT_REDEMPTION_FILTER,
  fulfilRedemption,
  redemptionsPath,
  submitCancellation,
  type RedemptionFilter,
} from './engagement';

const FILTERS: readonly RedemptionFilter[] = ['PENDING', 'FULFILLED', 'CANCELLED', 'ALL'];

/**
 * Redemption fulfilment (SPEC §9) — the queue staff work at the counter.
 *
 * Defaults to PENDING because that is the pile someone is waiting on. Both row
 * actions race against colleagues on other machines: a `not_pending` refusal
 * is not an error to apologise for, it means someone else got there first, so
 * the screen says that and refreshes.
 */
export function RedemptionsPage() {
  const { t, i18n } = useTranslation();

  const [filter, setFilter] = useState<RedemptionFilter>(DEFAULT_REDEMPTION_FILTER);
  const { data, loading, error, reload } = useResource<PrizeRedemptionDto[]>(
    redemptionsPath(filter),
  );
  const redemptions = data ?? [];

  const [fulfilling, setFulfilling] = useState<PrizeRedemptionDto | null>(null);
  const [cancelling, setCancelling] = useState<PrizeRedemptionDto | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const showReason = filter === 'CANCELLED' || filter === 'ALL';

  const dateTime = (iso: string) => {
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return '—';
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'short',
      timeStyle: 'short',
      numberingSystem: 'latn',
    }).format(parsed);
  };

  /** Someone on another machine already fulfilled or cancelled the row. */
  function handleStale(): void {
    setFulfilling(null);
    setCancelling(null);
    setNotice(t('portalContent.redemptions.notPending'));
    void reload();
  }

  function handleResolved(): void {
    setFulfilling(null);
    setCancelling(null);
    setNotice(null);
    void reload();
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            onClick={() => {
              setFilter(candidate);
              setNotice(null);
            }}
            data-status={candidate}
            aria-pressed={candidate === filter}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              candidate === filter
                ? 'border-primary bg-secondary font-medium text-primary'
                : 'border-subtle text-content-secondary hover:bg-hover'
            }`}
          >
            {t(`portalContent.redemptions.filters.${candidate.toLowerCase()}`)}
          </button>
        ))}
      </div>

      {notice ? (
        <p role="alert" className="mb-3 text-sm text-danger">
          {notice}
        </p>
      ) : null}

      {loading && redemptions.length === 0 ? (
        <p className="text-sm text-content-muted">{t('common.loading')}</p>
      ) : error ? (
        // Before the empty state: "the counter is clear" while the API is down
        // would be the worst possible lie on this screen.
        <p className="text-sm text-danger">{error}</p>
      ) : redemptions.length === 0 ? (
        <p className="text-sm text-content-muted">
          {filter === 'PENDING'
            ? t('portalContent.redemptions.emptyPending')
            : t('portalContent.redemptions.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr className="border-b border-subtle">
                <Th>{t('portalContent.redemptions.columns.redeemedAt')}</Th>
                <Th>{t('portalContent.redemptions.columns.customer')}</Th>
                <Th>{t('portalContent.redemptions.columns.prize')}</Th>
                <Th>{t('portalContent.redemptions.columns.points')}</Th>
                <Th>{t('portalContent.redemptions.columns.status')}</Th>
                <Th>{t('portalContent.redemptions.columns.employee')}</Th>
                {showReason ? <Th>{t('portalContent.redemptions.columns.reason')}</Th> : null}
                <Th>
                  <span className="sr-only">{t('common.actions')}</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {redemptions.map((redemption, index) => (
                <tr
                  key={redemption.id}
                  className={`border-b border-subtle/50 ${index % 2 === 1 ? 'bg-row-odd' : 'bg-row-even'}`}
                >
                  <Td>
                    <span className="tabular">{dateTime(redemption.redeemedAt)}</span>
                  </Td>
                  <Td>{redemption.customerName}</Td>
                  <Td>
                    <span className="font-medium">{redemption.prizeName}</span>
                  </Td>
                  <Td>
                    <span className="tabular">{redemption.pointsSpent}</span>
                  </Td>
                  <Td>
                    <StatusBadge status={redemption.status} />
                  </Td>
                  <Td>
                    {redemption.employeeName ?? <span className="text-content-muted">—</span>}
                  </Td>
                  {showReason ? (
                    <Td>
                      {redemption.status === 'CANCELLED' && redemption.cancelReason ? (
                        <span className="block max-w-64 truncate" title={redemption.cancelReason}>
                          {redemption.cancelReason}
                        </span>
                      ) : (
                        <span className="text-content-muted">—</span>
                      )}
                    </Td>
                  ) : null}
                  <Td>
                    {redemption.status === 'PENDING' ? (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setFulfilling(redemption)}
                          className={`${pillClass} px-3 py-1 text-xs`}
                        >
                          {t('portalContent.redemptions.fulfil')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCancelling(redemption)}
                          className={`${ghostPillClass} px-3 py-1 text-xs`}
                        >
                          {t('portalContent.redemptions.cancel')}
                        </button>
                      </div>
                    ) : null}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {fulfilling ? (
        <FulfilDialog
          redemption={fulfilling}
          onClose={() => setFulfilling(null)}
          onFulfilled={handleResolved}
          onStale={handleStale}
        />
      ) : null}

      {cancelling ? (
        <CancelDialog
          redemption={cancelling}
          onClose={() => setCancelling(null)}
          onCancelled={handleResolved}
          onStale={handleStale}
        />
      ) : null}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="whitespace-nowrap px-3 py-2 text-start text-xs font-medium uppercase tracking-wide text-content-muted"
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="whitespace-nowrap px-3 py-2 text-content">{children}</td>;
}

function StatusBadge({ status }: { status: PrizeRedemptionStatus }) {
  const { t } = useTranslation();
  const style = {
    PENDING: 'bg-secondary text-primary',
    FULFILLED: 'border border-subtle text-available',
    CANCELLED: 'border border-subtle text-danger',
  }[status];
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${style}`}>
      {t(`portalContent.redemptions.status.${status}`)}
    </span>
  );
}

/**
 * "Hand over <prize> to <name>?" — the confirm exists because fulfilment is
 * the moment goods leave the shelf; there is no undo short of cancelling.
 */
export function FulfilDialog({
  redemption,
  onClose,
  onFulfilled,
  onStale,
}: {
  redemption: PrizeRedemptionDto;
  onClose: () => void;
  onFulfilled: () => void;
  onStale: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleConfirm(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await fulfilRedemption(redemption.id);
      onFulfilled();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'not_pending') {
        onStale();
        return;
      }
      setError(
        caught instanceof ApiError ? caught.message : t('portalContent.redemptions.fulfilFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <HandCoins className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
          <div>
            <h2 className="text-lg font-semibold text-content">
              {t('portalContent.redemptions.fulfilTitle')}
            </h2>
            <p className="mt-1 text-sm text-content-secondary">
              {t('portalContent.redemptions.fulfilConfirm', {
                prize: redemption.prizeName,
                name: redemption.customerName,
              })}
            </p>
          </div>
        </div>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={ghostPillClass}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={submitting}
            className={pillClass}
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Cancelling needs a reason (contract: the row is the only record of why) and
 * the copy states up front that points and stock come back. Validation runs
 * through `submitCancellation`, which refuses an empty reason before any
 * request is made — the same zod schema the server applies.
 */
export function CancelDialog({
  redemption,
  onClose,
  onCancelled,
  onStale,
}: {
  redemption: PrizeRedemptionDto;
  onClose: () => void;
  onCancelled: () => void;
  onStale: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await submitCancellation(redemption.id, reason);
      if (!outcome.ok) {
        setError(outcome.message);
        return;
      }
      onCancelled();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'not_pending') {
        onStale();
        return;
      }
      setError(
        caught instanceof ApiError ? caught.message : t('portalContent.redemptions.cancelFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden />
          <div>
            <h2 className="text-lg font-semibold text-content">
              {t('portalContent.redemptions.cancelTitle')}
            </h2>
            <p className="mt-1 text-sm text-content-secondary">
              {t('portalContent.redemptions.cancelIntro', {
                points: redemption.pointsSpent,
                name: redemption.customerName,
                prize: redemption.prizeName,
              })}
            </p>
          </div>
        </div>

        <label className="mt-4 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('portalContent.redemptions.reasonLabel')}
          </span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={200}
            placeholder={t('portalContent.redemptions.reasonPlaceholder')}
            className={inputClass}
          />
        </label>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={ghostPillClass}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-full bg-danger px-4 py-2 text-sm font-medium text-white hover:bg-danger-hover disabled:opacity-60"
          >
            {t('portalContent.redemptions.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
